import { useState } from "react";
import { Settings, Loader2, FlaskConical, UserCircle, LogOut } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { Field, PageHeader, QtyInput, RupiahInput, SectionTitle } from "@/components/common";
import { SimButtons } from "@/components/SimActions";

function SetupForm() {
  const { ws, bump } = useAuth();
  const [name, setName] = useState(ws.business_name || "");
  const [cash, setCash] = useState(ws.opening_cash || 0);
  const [mats, setMats] = useState(ws.materials.map((m) => ({ key: m.key, name: m.name, unit: m.unit, min_stock: String(m.min_stock), initial_stock: String(m.initial_stock || "") })));
  const [busy, setBusy] = useState(false);
  const set = (i, p) => setMats((ms) => ms.map((m, j) => (j === i ? { ...m, ...p } : m)));
  const save = async () => {
    if (mats.some((m) => !m.unit.trim())) { toast.error("Satuan bahan tidak boleh kosong."); return; }
    setBusy(true);
    try {
      await api.put("/workspace/setup", { business_name: name, opening_cash: cash, materials: mats.map((m) => ({ key: m.key, unit: m.unit.trim(), min_stock: Number(m.min_stock) || 0, initial_stock: Number(m.initial_stock) || 0 })) });
      toast.success("Pengaturan usaha tersimpan."); bump();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="jt-card p-5 sm:p-6 space-y-4" data-testid="setup-form">
      <SectionTitle>Pengaturan Awal Usaha</SectionTitle>
      <p className="text-sm text-muted-foreground">Semua isian bersifat opsional dan bisa dilengkapi kapan saja. <b>Saldo Awal Uang Kas</b> adalah uang usaha yang Anda miliki saat mulai memakai aplikasi. Saldo awal dan stok awal hanya dihitung satu kali — jika diubah, nilai lama diganti, bukan ditambah.</p>
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Nama Usaha"><input className="jt-input" placeholder="Contoh: Pabrik Tahu Barokah" value={name} onChange={(e) => setName(e.target.value)} data-testid="setup-business-name" /></Field>
        <Field label="Saldo Awal Uang Kas"><RupiahInput value={cash} onChange={setCash} testid="setup-opening-cash" /></Field>
      </div>
      <div className="space-y-2.5">
        <div className="text-sm font-bold">Stok Awal Bahan Baku</div>
        {mats.map((m, i) => (
          <div key={m.key} className="grid grid-cols-3 sm:grid-cols-[1fr_1fr_1fr_1fr] gap-2 items-end rounded-xl border bg-white/60 p-3" data-testid={`setup-material-${m.key}`}>
            <div className="col-span-3 sm:col-span-1 font-extrabold text-[var(--jt-emerald)] sm:pb-3">{m.name}</div>
            <Field label="Stok awal"><QtyInput value={m.initial_stock} onChange={(v) => set(i, { initial_stock: v })} testid={`setup-initial-${m.key}`} /></Field>
            <Field label="Satuan"><input className="jt-input" value={m.unit} onChange={(e) => set(i, { unit: e.target.value })} data-testid={`setup-unit-${m.key}`} /></Field>
            <Field label="Batas minimum"><QtyInput value={m.min_stock} onChange={(v) => set(i, { min_stock: v })} testid={`setup-min-${m.key}`} /></Field>
          </div>
        ))}
      </div>
      <button className="jt-btn jt-btn-primary w-full sm:w-auto" disabled={busy} onClick={save} data-testid="setup-save-button">{busy && <Loader2 className="w-4 h-4 animate-spin" />} Simpan Pengaturan</button>
    </div>
  );
}

export default function Pengaturan() {
  const { ws, logout } = useAuth();
  return (
    <div data-testid="pengaturan-page">
      <PageHeader icon={Settings} title="Pengaturan" subtitle="Atur data awal usaha dan data simulasi." />
      <div className="grid xl:grid-cols-[1.4fr_1fr] gap-6">
        <SetupForm />
        <div className="space-y-6">
          <div className="jt-card p-5 sm:p-6 space-y-3" data-testid="simulation-settings">
            <SectionTitle><span className="flex items-center gap-2"><FlaskConical className="w-5 h-5 text-[#9a7420]" /> Data Simulasi</span></SectionTitle>
            <p className="text-sm text-muted-foreground" data-testid="simulation-status">
              {ws.simulation_mode ? "Akun ini sedang memakai data contoh untuk mencoba aplikasi." : "Akun ini memakai data asli. Anda bisa memuat data contoh lagi kapan saja dengan Reset Data Simulasi."}
            </p>
            <div className="flex flex-col sm:flex-row xl:flex-col gap-2"><SimButtons /></div>
          </div>
          <div className="jt-card p-5 sm:p-6 space-y-3">
            <SectionTitle><span className="flex items-center gap-2"><UserCircle className="w-5 h-5" /> Akun</span></SectionTitle>
            <div className="text-sm">Nama Akun: <b data-testid="settings-username">{ws.username}</b></div>
            <p className="text-xs text-muted-foreground">Anda tetap masuk sampai menekan Keluar.</p>
            <button className="jt-btn jt-btn-ghost" onClick={logout} data-testid="settings-logout-button"><LogOut className="w-4 h-4" /> Keluar</button>
          </div>
        </div>
      </div>
    </div>
  );
}
